// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { navigate } from 'gatsby';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    // Check localStorage first for existing session
    const checkStoredAuth = () => {
      try {
        const storedUser = localStorage.getItem('authUser');
        const storedToken = localStorage.getItem('authToken');
        
        // If we at least have a stored user, restore it. Token may be missing
        // in some flows; we'll still restore the user and attempt to refresh
        // the JWT from Netlify Identity when it's available.
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);

          // If token is missing but Netlify Identity exists, try to get a fresh JWT
          if (!storedToken && typeof window !== 'undefined' && window.netlifyIdentity) {
            const current = window.netlifyIdentity.currentUser();
            if (current && current.jwt) {
              current.jwt()
                .then(token => {
                  try { localStorage.setItem('authToken', token); } catch (e) { /* ignore */ }
                })
                .catch(() => {});
            }
          }

          setLoading(false);
          return true;
        }
      } catch (error) {
        console.error('Error reading stored auth:', error);
      }
      return false;
    };

    // Initialize Netlify Identity
    const initNetlifyIdentity = () => {
      // Check stored auth first
      if (checkStoredAuth()) {
        return;
      }

      if (window.netlifyIdentity) {
        window.netlifyIdentity.init();

        // Check for existing user
        const currentUser = window.netlifyIdentity.currentUser();
        
        if (currentUser) {
          handleAuthUser(currentUser);
        } else {
          setLoading(false);
        }

        // Set up event listeners
        window.netlifyIdentity.on('init', user => {
          if (user) {
            handleAuthUser(user);
          } else {
            setLoading(false);
          }
        });

        window.netlifyIdentity.on('login', user => {
          handleAuthUser(user);
          window.netlifyIdentity.close();
          
          // Redirect based on role
          if (user.app_metadata?.roles?.includes('admin')) {
            navigate('/admin/');
          } else {
            navigate('/');
          }
        });

        window.netlifyIdentity.on('logout', () => {
          setUser(null);
          localStorage.removeItem('authUser');
          localStorage.removeItem('authToken');
          navigate('/login/');
        });

        window.netlifyIdentity.on('error', (error) => {
          console.error('Netlify Identity error:', error);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    };

    // Load Netlify Identity Widget script if not already loaded
    const loadNetlifyIdentity = () => {
      if (window.netlifyIdentity) {
        initNetlifyIdentity();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://identity.netlify.com/v1/netlify-identity-widget.js';
      script.async = true;
      script.onload = () => {
        setTimeout(initNetlifyIdentity, 100);
      };
      script.onerror = () => {
        console.error('Failed to load Netlify Identity');
        setLoading(false);
      };
      document.body.appendChild(script);
    };

    loadNetlifyIdentity();

    return () => {
      // Cleanup event listeners
      if (window.netlifyIdentity) {
        window.netlifyIdentity.off('init');
        window.netlifyIdentity.off('login');
        window.netlifyIdentity.off('logout');
        window.netlifyIdentity.off('error');
      }
    };
  }, []);

  const handleAuthUser = (netlifyUser) => {
    if (!netlifyUser) {
      setLoading(false);
      return;
    }

    // Extract user role - check app_metadata.roles
    const roles = netlifyUser.app_metadata?.roles || [];
    const isAdmin = roles.includes('admin');

    const userData = {
      id: netlifyUser.id,
      email: netlifyUser.email,
      name: netlifyUser.user_metadata?.full_name || netlifyUser.email,
      role: isAdmin ? 'admin' : 'student',
      avatar: netlifyUser.user_metadata?.avatar_url,
      roles: roles,
    };

    // Get access token
    netlifyUser.jwt()
      .then(token => {
        localStorage.setItem('authToken', token);
        localStorage.setItem('authUser', JSON.stringify(userData));
        setUser(userData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error getting token:', error);
        // Still set user even if token fails
        localStorage.setItem('authUser', JSON.stringify(userData));
        setUser(userData);
        setLoading(false);
      });
  };

  const login = () => {
    if (window.netlifyIdentity) {
      window.netlifyIdentity.open('login');
    }
  };

  const signup = () => {
    if (window.netlifyIdentity) {
      window.netlifyIdentity.open('signup');
    }
  };

  const logout = () => {
    // Always perform local cleanup so logout works even if the Netlify
    // Identity widget isn't loaded (e.g., in some builds or SSR scenarios).
    try {
      localStorage.removeItem('authUser');
      localStorage.removeItem('authToken');
    } catch (e) {
      console.warn('Could not clear localStorage during logout', e);
    }

    setUser(null);

    // If Netlify Identity is available, call its logout which will also
    // fire the 'logout' event (we already handled local cleanup above).
    if (typeof window !== 'undefined' && window.netlifyIdentity) {
      try {
        window.netlifyIdentity.logout();
      } catch (e) {
        console.warn('Netlify Identity logout failed, redirecting manually', e);
        navigate('/login/');
      }
    } else {
      // Fallback redirect when the widget isn't present.
      navigate('/login/');
    }
  };

  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role || user.roles?.includes(role);
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        loading, 
        isAuthenticated,
        login,
        signup,
        logout,
        hasRole
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};